<template>
    <vx-expandable-card id="custom-attributes" class="max-w-(--breakpoint-sm)">
        <template v-slot:header> Custom Attributes </template>
        <template v-slot:headerSubtitle> Metadata that can be used in other resources. </template>

        <template v-slot:content>
            <div class="flex flex-col gap-2 mb-3 lg:flex-row lg:mb-6">
                <div class="lg:w-60">
                    <vx-label>Key</vx-label>
                    <vx-input
                        ref="keyElement"
                        size="lg"
                        v-model="key"
                        :disabled="!isAdminOrOwner"
                        :error="displayFirstError(validate.key)"
                        data-test="key"
                    ></vx-input>
                </div>
                <div class="flex grow gap-2">
                    <div class="grow">
                        <vx-label>Value</vx-label>
                        <vx-input
                            size="lg"
                            v-model="value"
                            :disabled="!isAdminOrOwner"
                            @keypress.enter="add()"
                            :error="displayFirstError(validate.value)"
                            data-test="value"
                        ></vx-input>
                    </div>
                    <div class="pt-7 lg:pt-6">
                        <vx-button
                            @click="add()"
                            data-test="custom-attribute-add"
                            aria-label="Add custom attribute"
                            :disabled="!isAdminOrOwner"
                            size="lg"
                            type="button"
                        >
                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>
            <vx-icon-loading v-if="loading" spin></vx-icon-loading>
            <vx-alert :closeable="false" v-else-if="!customAttributes.length">
                No custom attributes were added to this team.
            </vx-alert>
            <div v-else>
                <vx-list-animated
                    v-model="customAttributes"
                    value="key"
                    label="key"
                    :removeable="isAdminOrOwner"
                    @removed="onRemoved"
                >
                    <template v-slot:item="{ item }">
                        <div
                            class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2"
                        >
                            <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.key }}</div>
                            <div class="line-clamp-3 break-all">{{ item.value }}</div>
                        </div>
                    </template>
                </vx-list-animated>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { nextTick, ref } from 'vue';
import { teamCustomAttributesClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxExpandableCard,
    VxIconLoading,
    VxInput,
    VxLabel,
    VxListAnimated,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import {
    csvInjection,
    alphanumericDashUnderscore,
    teamCustomAttributeKeyFirstLetter,
    customAttributeValue,
    displayFirstError,
} from '~/utils/validation';

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;
const toasted = useToasted();

const loading = ref(false);
const adding = ref(false);

const key = ref('');
const value = ref('');
const customAttributes = ref([]);

const keyElement = ref();
const focusKeyElement = async () => {
    await nextTick();
    keyElement.value.field?.focus();
};

const validate = useVuelidate(
    {
        key: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(40),
            format: alphanumericDashUnderscore,
            teamCustomAttributeKeyFirstLetter: teamCustomAttributeKeyFirstLetter,
        },
        value: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(350),
            format: customAttributeValue,
            csvInjection: csvInjection,
        },
    },
    {
        key,
        value,
    }
);

const getList = async () => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    try {
        const response = await teamCustomAttributesClient.getAllCustomAttributes(teamId);
        customAttributes.value = response.data.data.sort((a, b) => (a.key > b.key ? 1 : -1));
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const add = async () => {
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();

    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    const attribute = {
        key: key.value.toLowerCase(),
        value: value.value,
    };

    try {
        const existingIndex = customAttributes.value.findIndex(
            (custtomAttribute) => custtomAttribute.key === attribute.key
        );
        if (existingIndex !== -1) {
            await teamCustomAttributesClient.updateCustomAttribute(teamId, attribute.key, { value: attribute.value });
            customAttributes.value.splice(existingIndex, 1);
        } else {
            await teamCustomAttributesClient.createCustomAttribute(teamId, attribute);
        }

        customAttributes.value.unshift(attribute);
        key.value = '';
        value.value = '';
        focusKeyElement();
    } catch (error) {
        if (error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(error?.response?.data?.message || 'Something went wrong.');
    } finally {
        adding.value = false;
    }
};

// VxListAnimated drops the item from the model before emitting `removed`, and the leaving row stays clickable
// while it animates out.
const removing = new Set();

const onRemoved = async (item) => {
    if (removing.has(item.key)) {
        return;
    }

    removing.add(item.key);

    try {
        await teamCustomAttributesClient.deleteCustomAttribute(teamId, item.key);
    } catch (error) {
        customAttributes.value.unshift(item);

        if (error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(error?.response?.data?.message || 'Something went wrong.');
    } finally {
        removing.delete(item.key);
    }
};

getList();
</script>
